import { Grid2, Tab, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import { ClientDetailsPanel } from "./components/client-detailsPanel";
import { ClientCareInfoPanel } from "./components/client-careInfoPanel";
import { ClientMedicalInfoPanel } from "./components/client-medicalInfoPanel";
import { ClientContactPanel } from "./components/client-contactPanel";
import { ClientFileUploadPanel } from "./components/client-filePanel";
import { ClientLogPanel } from "./components/client-logPanel";
import { ClientFormPanel } from "./components/client-formPanel";
import { clientService } from "../../_services/client.service";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { StandardViewPanel } from "../../_components/panel/standardView.panel";
import { StandardPanel } from "../../_components/panel/standard.panel";

const tabListSx = {
    backgroundColor: "grey.100",
    borderRadius: 1,
    minHeight: 48,
    "& .MuiTabs-indicator": {
        display: "none",
    },
    "& .MuiTab-root": {
        flex: 1,
        minHeight: "48px",
        fontWeight: 500,
        borderRadius: 1,
        "&.Mui-selected": {
            backgroundColor: "primary.main",
            color: "white",
        },
    },
} as const;

export const ClientView = () => {
    const { clientId } = useParams<{ clientId: string }>();
    const [value, setValue] = useState("1");

    const [data, setData] = useState<any>({});
    const [isLoading, setIsLoading] = useState(true);

    const canLoad = !!data.id;

    const fetchClient = () => {
        if(!clientId) return;
        setIsLoading(true);
        clientService.getLatest(clientId).then(setData).catch(handleErrorResponse).finally(()=> setIsLoading(false));
    }

    useEffect(() => {
        fetchClient();
    }, [clientId]);

    const handleChange = (event: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
    };

    return (
        <TabContext value={value}>
            <Grid2 container spacing={1} p={2}>
                <Grid2
                    size={{ xs: 12, md: 5 }}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        minWidth: 0,
                    }}
                >
                    <Typography
                        component="div"
                        variant="subtitle1"
                        fontWeight={600}
                        sx={{
                            width: "100%",
                            minWidth: 0,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            lineHeight: 1.2,
                        }}
                        title={data.centreName || undefined}
                    >
                        {data.centreName || "\u00a0"}
                    </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 7 }} sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                    <TabList onChange={handleChange} variant="fullWidth" sx={tabListSx}>
                        <Tab label="Client Contacts" value="1" />
                        <Tab label="Forms" value="2" />
                        <Tab label="Logs" value="3" />
                        <Tab label="Files" value="4" />
                    </TabList>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 4, md: 2.5 }}>
                    {canLoad
                        ? <ClientDetailsPanel data={data} isLoading={isLoading} onRefresh={fetchClient} />
                        : <StandardViewPanel title="Client Details" items={[]} buttons={[]} loading={isLoading} />}
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 4, md: 2.5 }}>
                    {canLoad
                        ? <ClientCareInfoPanel data={data} isLoading={isLoading} onRefresh={fetchClient} />
                        : <StandardViewPanel title="Care Info" items={[]} buttons={[]} loading={isLoading} />}
                    {canLoad
                        ? <ClientMedicalInfoPanel data={data} isLoading={isLoading} onRefresh={fetchClient} />
                        : <StandardViewPanel title="Medical Info" items={[]} buttons={[]} loading={isLoading} />}
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 8, md: 7 }}>
                    <TabPanel value="1" sx={{ p: 0 }}>
                        <Grid2 container spacing={1}>
                            <Grid2 size={{ xs: 12, sm: 6, md: 12 }}>
                                {canLoad
                                    ? <ClientContactPanel />
                                    : <StandardPanel title="Contacts" loading={false} />}
                            </Grid2>
                        </Grid2>
                    </TabPanel>
                    <TabPanel value="2" sx={{ p: 0}}>
                        <Grid2 container spacing={1}>
                            <Grid2 size={{ xs: 12, sm: 6, md: 12 }}>
                                {canLoad
                                    ? <ClientFormPanel />
                                    : <StandardPanel title="Client Forms" loading={false} />}
                            </Grid2>
                        </Grid2>
                    </TabPanel>
                    <TabPanel value="3" sx={{ p: 0}}>
                        <Grid2 container spacing={1}>
                            <Grid2 size={{ xs: 12, sm: 6, md: 12 }}>
                                {canLoad
                                    ? <ClientLogPanel />
                                    : <StandardPanel title="Logs" loading={false} />}
                            </Grid2>
                        </Grid2>
                    </TabPanel>
                    <TabPanel value="4" sx={{ p: 0}}>
                        <Grid2 container spacing={1}>
                            <Grid2 size={{ xs: 12, sm: 6, md: 12 }}>
                                {canLoad
                                    ? <ClientFileUploadPanel />
                                    : <StandardPanel title="Files" loading={false} />}
                            </Grid2>
                        </Grid2>
                    </TabPanel>
                </Grid2>
            </Grid2>
        </TabContext>
    );
};